import { useCallback, useEffect, useState } from 'react'
import { ChargeCategoryFragment, OvbResolveType, ReservedResourceMultiInput, useChargeCategoriesQuery, useUpdateReservedResourcesMutation } from 'src/api/api.generated'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'
import { useTranslation } from 'react-i18next'
import { RRBulkEditCCGridRow } from './rr-bulk-edit-cc-grid-rows'
import { reservedResourceToReservedResourceMultiInput } from '../../../reserved-resources-bulk-edit/reserved-resources-bulk-edit-utils'
import { useApolloClient } from '@apollo/client'

export default function useRRBulkEditChargeCategoryGridCRUD(propertyId: string, onRRUpdated?: () => Promise<void>) {
  const { id: tenantId } = useTenant()
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const showError = useErrorSnackBar()
  const showSuccess = useSuccessSnackBar()
  const apolloClient = useApolloClient()

  const [stayIncludedCC, setStayIncludedCC] = useState<ChargeCategoryFragment[]>([])
  const { loading: loadingChargeCategories } = useChargeCategoriesQuery({
    variables: {
      filter: {
        tenantId: {
          eq: tenantId,
        },
        propertyId: {
          eq: propertyId,
        },
        stayIncluded: true,
      },
    },
    onCompleted: (data) => {
      if (data.chargeCategories) setStayIncludedCC(data.chargeCategories)
    },
  })

  const [updateReservedResources] = useUpdateReservedResourcesMutation({
    refetchQueries: ['ReservationDetail'],
  })

  const updateRRs = useCallback(
    async (rows: RRBulkEditCCGridRow[]) => {
      setLoading(true)
      const reservedResourceMultiInput: ReservedResourceMultiInput[] = rows.map((row) => {
        const input = reservedResourceToReservedResourceMultiInput(tenantId, row.reservedResource)
        return {
          ...input,
          reservedResource: {
            ...input.reservedResource,
            chargeCategories: row.chargeCategoriesPerDay
              .filter((ccd) => ccd)
              .map((ccd) => {
                return {
                  chargeCategoryId: ccd?.chargeCategoryId ?? ccd!.initialChargeCategoryId,
                  day: ccd!.day,
                }
              }),
          },
        }
      })

      try {
        const result = await updateReservedResources({
          variables: {
            value: reservedResourceMultiInput,
            options: {
              overbooking: OvbResolveType.ERROR,
            },
          },
        })
        if (result.errors) {
          showError(result.errors)
        } else if (result.data) {
          showSuccess(t('pms.booking.reservation.rr_bulk_edit_dialog.form.success_message'))
          await onRRUpdated?.()
        }
      } catch (e) {
        showError(e)
      }
      setLoading(false)
    },
    [updateReservedResources],
  )

  useEffect(() => {
    setLoading(loadingChargeCategories)
  }, [loadingChargeCategories])

  return { stayIncludedCC, updateRRs, loading }
}
